/**
 * The studentized range distribution — the engine behind Tukey's HSD and the Nemenyi
 * post hoc test.
 *
 * If x₁ … x_k are independent N(μ, σ²) draws and s² is an independent estimate of σ² on
 * `df` degrees of freedom, the studentized range is q = (max xᵢ − min xᵢ) / s. This module
 * follows the algorithm of Copenhaver & Holland (1988), "Computation of the distribution of
 * the maximum of independent normal variables" — the same Gauss–Legendre quadrature that R's
 * `ptukey` (nmath/ptukey.c) uses, with `wprob` integrating the range of k normals and the
 * outer integral running over the chi distribution of s.
 *
 * - `ptukey(q, k, df)`: P(Q ≤ q); `df = Infinity` is the range of k standard normals
 *   (used by the Nemenyi test).
 * - `qtukey(p, k, df)`: the quantile, by Newton–secant iteration from the starting value
 *   of Odeh & Evans (1974), as in R's `qtukey`, but iterated to 1e-10 rather than R's
 *   1e-4 so the quantile is limited by the CDF's accuracy (about 1e-12), not the stopping rule.
 *
 * The `nlegendre` abscissae and weights are the 12- and 16-point Gauss–Legendre rules
 * from the published algorithm. Pinned against R 4.5.0 `ptukey`/`qtukey` and SciPy
 * `studentized_range` in `stats-tests-more.test.ts`.
 */

import { normalCdf } from "@/lib/stats/distributions";
import { erf, logGamma } from "@/lib/stats/special";

/* ----------------------------------------------------- 12-point rule for wprob */

const XLEG = [
  0.981_560_634_246_719_4, 0.904_117_256_370_474_9, 0.769_902_674_194_304_7,
  0.587_317_954_286_617_4, 0.367_831_498_998_180_2, 0.125_233_408_511_468_9,
];
const ALEG = [
  0.047_175_336_386_511_83, 0.106_939_325_995_318_43, 0.160_078_328_543_346_22,
  0.203_167_426_723_065_92, 0.233_492_536_538_354_8, 0.249_147_045_813_402_8,
];
const LEG_HALF = 6;
const LEG_ORDER = 12;

const W_C1 = -30;
const W_C2 = -50;
const W_C3 = 60;
const W_UPPER = 8;
const W_LARGE = 3;
const ONE_OVER_SQRT_2PI = 0.398_942_280_401_432_7;

/**
 * P(range of `cc` standard normals ≤ w), raised to the power `rr` (the number of
 * independent ranges; 1 for the usual Tukey test).
 */
function wprob(w: number, rr: number, cc: number): number {
  const qsqz = w * 0.5;
  // Beyond w = 16 the integral's lower bound is 0.99999999999995.
  if (qsqz >= W_UPPER) {
    return 1;
  }
  let prW = erf(qsqz / Math.SQRT2);
  prW = prW >= Math.exp(W_C2 / cc) ? prW ** cc : 0;

  const intervals = w > W_LARGE ? 2 : 3;
  let lower = qsqz;
  const width = (W_UPPER - qsqz) / intervals;
  let upper = lower + width;
  let total = 0;
  const cc1 = cc - 1;
  for (let wi = 1; wi <= intervals; wi++) {
    let sum = 0;
    const a = 0.5 * (upper + lower);
    const b = 0.5 * (upper - lower);
    for (let jj = 1; jj <= LEG_ORDER; jj++) {
      let j = jj;
      let xx = -XLEG[jj - 1];
      if (jj > LEG_HALF) {
        j = LEG_ORDER - jj + 1;
        xx = XLEG[j - 1];
      }
      const ac = a + b * xx;
      const qexpo = ac * ac;
      if (qexpo > W_C3) {
        break;
      }
      const rinsum = normalCdf(ac) - normalCdf(ac, w, 1);
      if (rinsum >= Math.exp(W_C1 / cc1)) {
        sum += ALEG[j - 1] * Math.exp(-(0.5 * qexpo)) * rinsum ** cc1;
      }
    }
    total += sum * (2 * b * cc * ONE_OVER_SQRT_2PI);
    lower = upper;
    upper += width;
  }
  prW += total;
  if (prW <= Math.exp(W_C1 / rr)) {
    return 0;
  }
  prW **= rr;
  return prW >= 1 ? 1 : prW;
}

/* --------------------------------------------------- 16-point rule for ptukey */

const XLEGQ = [
  0.989_400_934_991_649_9, 0.944_575_023_073_232_6, 0.865_631_202_387_831_8,
  0.755_404_408_355_003, 0.617_876_244_402_643_7, 0.458_016_777_657_227_4,
  0.281_603_550_779_258_9, 0.095_012_509_837_637_44,
];
const ALEGQ = [
  0.027_152_459_411_754_096, 0.062_253_523_938_647_89, 0.095_158_511_682_492_79,
  0.124_628_971_255_533_88, 0.149_595_988_816_576_74, 0.169_156_519_395_002_54,
  0.182_603_415_044_923_6, 0.189_450_610_455_068_5,
];
const LEGQ_HALF = 8;
const LEGQ_ORDER = 16;

const Q_EPS1 = -30;
const Q_EPS2 = 1e-14;
const DF_LARGE = 25_000;
const MAX_INTERVALS = 50;

function intervalLength(df: number): number {
  if (df <= 100) {
    return 1;
  }
  if (df <= 800) {
    return 0.5;
  }
  return df <= 5000 ? 0.25 : 0.125;
}

/**
 * The Gauss–Legendre sum over one outer interval i: the chi-density of s weighting the
 * range probability wprob(q·s).
 */
function intervalSum(
  q: number,
  k: number,
  ranges: number,
  i: number,
  ulen: number,
  f2lf: number,
  f21: number,
  ff4: number
): number {
  let otsum = 0;
  const twa1 = (2 * i - 1) * ulen;
  for (let jj = 1; jj <= LEGQ_ORDER; jj++) {
    const upperHalf = jj > LEGQ_HALF;
    const j = upperHalf ? jj - LEGQ_HALF - 1 : jj - 1;
    const offset = XLEGQ[j] * ulen;
    const position = upperHalf ? twa1 + offset : twa1 - offset;
    const t1 = upperHalf
      ? f2lf + f21 * Math.log(position) - position * ff4
      : f2lf + f21 * Math.log(position) + (offset - twa1) * ff4;
    if (t1 >= Q_EPS1) {
      otsum +=
        wprob(q * Math.sqrt(position * 0.5), ranges, k) *
        ALEGQ[j] *
        Math.exp(t1);
    }
  }
  return otsum;
}

/** P(Q ≤ q) for the studentized range of `k` means on `df` degrees of freedom. */
export function ptukey(q: number, k: number, df: number, ranges = 1): number {
  if (Number.isNaN(q) || Number.isNaN(df) || df < 2 || k < 2 || ranges < 1) {
    return Number.NaN;
  }
  if (q <= 0) {
    return 0;
  }
  if (!Number.isFinite(q)) {
    return 1;
  }
  if (df > DF_LARGE) {
    return wprob(q, ranges, k);
  }
  const f2 = df * 0.5;
  const ulen = intervalLength(df);
  const f2lf =
    f2 * Math.log(df) - df * Math.LN2 - logGamma(f2) + Math.log(ulen);
  const f21 = f2 - 1;
  const ff4 = df * 0.25;

  let ans = 0;
  for (let i = 1; i <= MAX_INTERVALS; i++) {
    const otsum = intervalSum(q, k, ranges, i, ulen, f2lf, f21, ff4);
    // Stop once an interval adds almost nothing — but never before one unit length.
    if (i * ulen >= 1 && otsum <= Q_EPS2) {
      break;
    }
    ans += otsum;
  }
  return Math.min(1, ans);
}

/** The upper-tail probability P(Q > q): the p-value of a Tukey comparison. */
export function ptukeyUpper(
  q: number,
  k: number,
  df: number,
  ranges = 1
): number {
  return 1 - ptukey(q, k, df, ranges);
}

/* ------------------------------------------------------------------ quantile */

// Odeh & Evans (1974) rational approximation to the normal quantile, and the
// Copenhaver–Holland starting value for the studentized-range quantile.
const P0 = 0.322_232_421_088;
const Q0 = 0.099_348_462_606;
const P1 = -1;
const Q1 = 0.588_581_570_495;
const P2 = -0.342_242_088_547;
const Q2 = 0.531_103_462_366;
const P3 = -0.204_231_210_125;
const Q3 = 0.103_537_752_85;
const P4 = -4.536_422_101_48e-5;
const Q4 = 3.856_070_063_4e-3;
const V_MAX = 120;

function startingValue(p: number, k: number, df: number): number {
  const ps = 0.5 - 0.5 * p;
  const yi = Math.sqrt(Math.log(1 / (ps * ps)));
  let t =
    yi +
    ((((yi * P4 + P3) * yi + P2) * yi + P1) * yi + P0) /
      ((((yi * Q4 + Q3) * yi + Q2) * yi + Q1) * yi + Q0);
  if (df < V_MAX) {
    t += (t ** 3 + t) / df / 4;
  }
  let q = 0.8832 - 0.2368 * t;
  if (df < V_MAX) {
    q += -1.214 / df + (1.208 * t) / df;
  }
  return t * (q * Math.log(k - 1) + Math.SQRT2);
}

const QUANTILE_TOLERANCE = 1e-10;
const QUANTILE_ITERATIONS = 100;

/** The q with P(Q ≤ q) = p — the critical value for a Tukey interval is `qtukey(1 − α, k, df)`. */
export function qtukey(p: number, k: number, df: number): number {
  if (Number.isNaN(p) || df < 2 || k < 2 || p < 0 || p > 1) {
    return Number.NaN;
  }
  if (p === 0) {
    return 0;
  }
  if (p === 1) {
    return Number.POSITIVE_INFINITY;
  }
  let x0 = startingValue(p, k, df);
  let f0 = ptukey(x0, k, df) - p;
  let x1 = f0 > 0 ? Math.max(0, x0 - 1) : x0 + 1;
  let f1 = ptukey(x1, k, df) - p;
  for (let i = 0; i < QUANTILE_ITERATIONS; i++) {
    if (f1 === f0) {
      break;
    }
    let next = x1 - (f1 * (x1 - x0)) / (f1 - f0);
    f0 = f1;
    x0 = x1;
    if (next < 0) {
      next = 0;
      f1 = -p;
    } else {
      f1 = ptukey(next, k, df) - p;
    }
    x1 = next;
    if (Math.abs(x1 - x0) < QUANTILE_TOLERANCE) {
      break;
    }
  }
  return x1;
}
